import React, { useEffect, useState } from 'react';
import { Paper, Grid, Typography, Button, TextField } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { appListing, getUserbyId, updateListedUser } from '../../api/UserApiCalls';
import { AppsPermissions } from '../invitation/appPermissions';
import { useNavigate, useParams } from 'react-router-dom';

const UpdateUserPage = () => {
  const [userData, setUserData] = useState(null);
  const [parsedJson, setParsedJson] = useState({});
  const [apps, setApps] = useState([]);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const { id: userId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAppsData = async () => {
      try {
        const appsResponse = await appListing();
        const userResponse = await getUserbyId(userId);

        if (appsResponse?.data && userResponse) {
          const userAppsMap = new Map(userResponse.userApps?.map((app) => [app.appId, app.userJson || '']));

          setApps(
            appsResponse.data.map((app) => ({
              id: app.id,
              name: app.name,
              userJson: userAppsMap.get(app.id) || ''
            }))
          );

          setUserData(userResponse);
          if (userResponse?.userApps) {
            setParsedJson(JSON.parse(userResponse.userApps));
          }
        }
      } catch (error) {
        console.error('Error fetching apps or user data:', error);
      }
    };

    fetchAppsData();
  }, [userId]);

  const validationSchema = Yup.object().shape({
    ...(isPasswordVisible
      ? {
          password: Yup.string().required('Password is required').min(8, 'Password must be at least 8 characters'),
          confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
            .required('Confirm Password is required')
            .min(8, 'Confirm Password must be at least 8 characters')
        }
      : {}),
    apps: Yup.object()
  });

  const handleSubmit = async (values) => {
    if (userId) {
      await updateListedUser(userId, values);
      navigate('/admin/users');
    }
  };

  return (
    <>
      <Typography variant="h5" align="left" sx={{ marginTop: '30px', marginLeft: '30px' }}>
        Update User
      </Typography>
      {userData && (
        <Formik
          enableReinitialize
          initialValues={{
            email: userData.email || '',
            username: userData.username || '',
            apps: Object.keys(parsedJson).length
              ? parsedJson
              : Object.fromEntries(apps.map((app) => [`app_${app.id}`, app.userJson || ''])),
            password: '',
            confirmPassword: ''
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form>
              <Paper elevation={0} sx={{ padding: 4, maxWidth: '1300px', margin: '30px' }}>
                <Grid container direction="column" spacing={2}>
                  <Grid item xs={5}>
                    <Button
                      size="medium"
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsPasswordVisible((prev) => !prev);
                        if (isPasswordVisible) {
                          setFieldValue('password', '');
                          setFieldValue('confirmPassword', '');
                        }
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      {isPasswordVisible ? 'Remove Password' : 'Update Password'}
                    </Button>
                  </Grid>
                  <Grid container spacing={2} direction="row" sx={{ marginTop: '20px' }}>
                    <Grid item xs={5}>
                      <Field
                        name="email"
                        as={TextField}
                        label="Email"
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: true }}
                        sx={{ mb: 3, width: '100%', backgroundColor: 'lightgrey' }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <Field
                        name="username"
                        as={TextField}
                        label="Username"
                        variant="outlined"
                        fullWidth
                        inputProps={{ readOnly: true }}
                        sx={{ mb: 3, width: '100%', backgroundColor: 'lightgrey' }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                    {isPasswordVisible && (
                      <>
                        <Grid item xs={5}>
                          <Field
                            name="password"
                            as={TextField}
                            label="Password"
                            type="password"
                            fullWidth
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <Field
                            name="confirmPassword"
                            as={TextField}
                            label="Confirm Password"
                            type="password"
                            fullWidth
                            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Paper>

              <AppsPermissions values={values} setFieldValue={setFieldValue} apps={apps} />

              <Paper elevation={0} sx={{ padding: 4, maxWidth: '1300px', margin: '30px' }}>
                <Grid container>
                  <Grid item>
                    <Button
                      size="medium"
                      color="error"
                      variant="contained"
                      sx={{ marginRight: '30px' }}
                      onClick={() => navigate('/admin/users')}
                    >
                      Cancel
                    </Button>
                    <Button size="medium" color="primary" variant="contained" type="submit">
                      Update
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default UpdateUserPage;
